import { MenuStatusType, MenuTypeType } from "@me-team/host/main/graphql/types";
import { TFunction } from "i18next";

interface MenuTypeWithSlug {
    slug?: string | null;
}

interface MenuTypeWithName extends MenuTypeWithSlug {
    name?: string | null;
}

interface MenuTypeEnumOption {
    value: MenuTypeType;
    label: string;
}

const menuTypeSlugToEnumMap: Record<string, MenuTypeType> = {
    delivery: MenuTypeType.Delivery,
    "for-establishment": MenuTypeType.DineIn,
    "self-delivery": MenuTypeType.Pickup,
};

const menuStatusSlugToEnumMap: Record<string, MenuStatusType> = {
    activated: MenuStatusType.Activated,
    deactivated: MenuStatusType.Deactivated,
    "under-development": MenuStatusType.UnderDevelopment,
};

export const toMenuTypeEnum = (slug?: string | null): MenuTypeType | null => {
    if (!slug) return null;
    return menuTypeSlugToEnumMap[slug.trim().toLowerCase()] ?? null;
};

export const toMenuStatusEnum = (slug?: string | null): MenuStatusType | null => {
    if (!slug) return null;
    return menuStatusSlugToEnumMap[slug.trim().toLowerCase()] ?? null;
};

export const getMenuTypeEnums = (menuTypes?: MenuTypeWithSlug[] | null): MenuTypeType[] => {
    return (menuTypes ?? [])
        .map((menuType) => toMenuTypeEnum(menuType.slug))
        .filter((value): value is MenuTypeType => value !== null);
};

export const getMenuTypeEnumOptionsBySlug = (
    menuTypes?: MenuTypeWithName[] | null,
    getLabel?: (name: string) => string
): MenuTypeEnumOption[] => {
    return (menuTypes ?? [])
        .map((menuType) => {
            const value = toMenuTypeEnum(menuType.slug);

            if (!value || !menuType.name) {
                return null;
            }

            return {
                value,
                label: getLabel ? getLabel(menuType.name) : menuType.name,
            };
        })
        .filter((option): option is MenuTypeEnumOption => option !== null);
};

export const isMenuActivated = (menuStatusSlug?: string | null): boolean => {
    const status = toMenuStatusEnum(menuStatusSlug);
    return status === MenuStatusType.Activated;
};

export const isMenuUnderDevelopment = (menuStatusSlug?: string | null): boolean => {
    const status = toMenuStatusEnum(menuStatusSlug);
    return status === MenuStatusType.UnderDevelopment;
};

export const canEditMenuContentByStatus = (
    menuStatusSlug: string | null | undefined,
    canCreateMenu: boolean,
    canCloneEditMenu: boolean
): boolean => {
    return canCloneEditMenu || (canCreateMenu && isMenuUnderDevelopment(menuStatusSlug));
};

export const getMenuTypeLabel = (menuType: MenuTypeType | null, t: TFunction): string => {
    const map = {
        [MenuTypeType.DineIn]: t("Dine-In menu"),
        [MenuTypeType.Delivery]: t("Delivery menu"),
        [MenuTypeType.Pickup]: t("Pickup menu"),
    };

    return menuType ? map[menuType] : t("Menu for establishment");
};
